import type { IChannelReadState } from '@commenda/types/src/chat-room/types';
import { SWRFetcher } from '@commenda/utils';
import type { ChatRoomType } from '@prisma/client';
import { useMemo } from 'react';
import useSWR from 'swr';

export interface IAdminChatRoomCompany {
  id: number;
  displayName: string | null;
  country: string | null;
}

export interface IAdminChatRoomAgentFirm {
  id: string;
  name: string;
}

export interface IAdminChatRoomRow {
  id: string;
  type: ChatRoomType;
  chatRoomName: string;
  isClosed: boolean;
  unreadMessagesCount: number;
  internalNotesUnreadCount: number;
  channels?: IChannelReadState[];
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  markedUnreadAt: string | null;
  isPinnedByUser: boolean;
  pinnedAt: string | null;
  company: IAdminChatRoomCompany | null;
  serviceRequest?: {
    id: string;
    isCommendaFiling: boolean;
    assignedAgentFirmId?: string | null;
  };
  agentFirm: IAdminChatRoomAgentFirm | null;
  hasPinnedMessages: boolean;
  hasCustomerInternalActivity: boolean;
}

export interface IAdminChatRoomsPagination {
  nextCursor: string | null;
  hasNextPage: boolean;
}

export interface IAdminChatRoomsCounts {
  myChats: number;
}

export interface IAdminChatRoomsResponse {
  chatRooms: IAdminChatRoomRow[];
  pagination: IAdminChatRoomsPagination;
  counts: IAdminChatRoomsCounts;
}

export type AdminChatRoomsReadStatus = 'all' | 'read' | 'unread';

export interface UseAdminChatRoomsOptions {
  limit?: number;
  cursor?: string;
  search?: string;
  readStatus?: AdminChatRoomsReadStatus;
  excludeEmptyChatRooms?: boolean;
  enabled?: boolean;
}

function buildAdminChatRoomsQueryString(
  options?: UseAdminChatRoomsOptions
): string | null {
  if (!options || options.enabled === false) return null;

  const params = new URLSearchParams();

  if (options.limit) {
    params.set('paginationConfig[limit]', String(options.limit));
  }
  if (options.cursor) {
    params.set('paginationConfig[cursor]', options.cursor);
  }
  if (options.search) {
    params.set('filterConfig[search]', options.search);
  }
  if (options.readStatus && options.readStatus !== 'all') {
    params.set('filterConfig[readStatus]', options.readStatus);
  }
  if (options.excludeEmptyChatRooms !== undefined) {
    params.set(
      'filterConfig[excludeEmptyChatRooms]',
      String(options.excludeEmptyChatRooms)
    );
  }

  const qs = params.toString();
  return qs ? `admin/account-managers/chat-rooms?${qs}` : 'admin/account-managers/chat-rooms';
}

export const useAdminChatRooms = (options?: UseAdminChatRoomsOptions) => {
  const key = buildAdminChatRoomsQueryString(options);

  const { data, error, mutate, isLoading, isValidating } =
    useSWR<IAdminChatRoomsResponse>(
      key,
      key ? SWRFetcher({ method: 'GET' }, undefined) : null,
      { dedupingInterval: 2000, revalidateOnFocus: true }
    );

  const chatRooms = useMemo<IAdminChatRoomRow[]>(() => {
    if (!data) return [];
    return data.chatRooms ?? [];
  }, [data]);

  return {
    chatRooms,
    pagination: data?.pagination ?? null,
    counts: data?.counts ?? null,
    error,
    mutate,
    isLoading: !!isLoading,
    isValidating: !!isValidating,
  };
};
